import { ActionReducerMapBuilder, createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { batch } from 'react-redux';
import {
  ConsentHubConsentAndSubscriptionRequest,
  ConsentHubConsentAndSubscriptionResponse,
  ConsentHubConsentRequest,
  ConsentHubGroup,
  ConsentHubSubscriptionRequest,
  fetchPreferences,
  fetchSessionPreferences,
  GroupType,
  ItemStatus,
  sendGlobalUnsubscribe,
  sendPreferencesUpdate,
  sendUnsubscribeList,
  updateConsent,
} from 'services/api';
import { AppDispatch, StoreType } from 'store';
import { getMockUserPreferences } from '../../services/api/mockData';
import { parsePreferencesQuery } from './services/referralUrlQuery';
import {
  trackLoadPreferencesCenterPage,
  trackRevokeAllConsentClick,
  trackRevokeConsentClick,
  trackSubscriptionGroupUpdate,
  trackSubscriptionUpdate,
} from './services/userActionEvents';
import {
  ActionResult,
  DialogPayload,
  ItemMeta,
  ItemUpdateState,
  ListUpdate,
  Status,
  SubscriptionReferralUrlParams,
  TriState,
  VisibleGroup,
} from './types';
import {
  getErrorNotificationData,
  getSuccessNotificationData,
  NotificationData,
} from '../../components/LayoutWrapper/components/notifications/notifications';
import {
  PREFERENCES_INITIALIZATION_PREFIX,
  PREFERENCES_LOAD_USER_PREFIX,
  PREFERENCES_SEND_FROM_UI_PREFIX,
  PREFERENCES_SLICE_NAME,
  PREFERENCES_SUBSCRIBE_PREFIX,
  PREFERENCES_UNSUBSCRIBE_ALL_PREFIX,
  PREFERENCES_UNSUBSCRIBE_PREFIX,
} from './constants';
import axios from 'axios';

interface PreferencesState {
  urlQueryParams: SubscriptionReferralUrlParams | undefined;
  hid: string | undefined;
  email: string | undefined;
  groups: Record<string, ConsentHubGroup>;
  initialStates: Record<string, ItemStatus | undefined>;
  status: Status;
  filter: VisibleGroup;
  actionResult: ActionResult | undefined;
  consentDialog: DialogPayload;
  consentDeclinePrDialog: DialogPayload;
  globalUnsubscribeDialog: DialogPayload;
  doubleOptInDialog: DialogPayload;
  notifications: NotificationData[];
}

const initialState: PreferencesState = {
  urlQueryParams: undefined,
  hid: undefined,
  email: undefined,
  groups: {},
  initialStates: {},
  status: {
    status: 'ok',
  },
  filter: { groupKey: 'all' },
  actionResult: undefined,
  consentDialog: {
    showDialog: false,
    update: undefined,
  },
  consentDeclinePrDialog: {
    showDialog: false,
    update: undefined,
  },
  globalUnsubscribeDialog: {
    showDialog: false,
    update: undefined,
  },
  doubleOptInDialog: {
    showDialog: false,
    update: undefined,
  },
  notifications: [],
};

interface ThunkApiConfig {
  state: StoreType;
  dispatch: AppDispatch;
  rejectValue: string | null;
}

export const loadUserPreferences = createAsyncThunk<ConsentHubConsentAndSubscriptionResponse, string | undefined>(
  PREFERENCES_LOAD_USER_PREFIX,
  async (hid, { rejectWithValue }) => {
    const mockUserPreferences = getMockUserPreferences(hid);
    if (mockUserPreferences) {
      return mockUserPreferences;
    }
    try {
      if (hid) {
        return await fetchPreferences(hid);
      } else {
        return (await fetchSessionPreferences()).data;
      }
    } catch (e) {
      if (axios.isAxiosError(e)) {
        return rejectWithValue(e.status);
      } else {
        throw e;
      }
    }
  },
);

export const unsubscribeAll = createAsyncThunk<void, void, ThunkApiConfig>(
  PREFERENCES_UNSUBSCRIBE_ALL_PREFIX,
  async (_, { getState, rejectWithValue, dispatch }) => {
    const hid = getState().preferences?.hid;

    if (!hid) return rejectWithValue(null);

    const userPreferences = getMockUserPreferences(hid);

    if (userPreferences == null) {
      const groups = getState().preferences.groups;

      await sendGlobalUnsubscribe(hid);

      // mark all subscriptions as unsubscribed and revoke all consents
      Object.keys(groups).forEach((groupKey) => {
        if (groups[groupKey].type === GroupType.SUBSCRIPTIONS || groups[groupKey].type === GroupType.CONSENTS) {
          Object.keys(groups[groupKey].tabs).forEach((tabKey) => {
            Object.keys(groups[groupKey].tabs[tabKey].items).forEach((itemKey) => {
              dispatch(
                setItemState({
                  groupKey: groupKey,
                  tabKey: tabKey,
                  itemKey: itemKey,
                  state: TriState.false,
                }),
              );
            });
          });
        }
      });
    } else if (hid === '5') {
      return rejectWithValue(null);
    }
  },
);

const findKey = (state: PreferencesState, targetKey: string): ItemMeta => {
  for (const groupKey of Object.keys(state.groups)) {
    for (const tabKey of Object.keys(state.groups[groupKey].tabs)) {
      for (const itemKey of Object.keys(state.groups[groupKey].tabs[tabKey].items)) {
        if (itemKey === targetKey) {
          return {
            groupKey: groupKey,
            tabKey: tabKey,
            itemKey: itemKey,
          };
        }
      }
    }
  }

  throw new Error("Item key '" + targetKey + "' not found.");
};

export const unsubscribe = createAsyncThunk<string[], void, ThunkApiConfig>(
  PREFERENCES_UNSUBSCRIBE_PREFIX,
  async (_, { dispatch, getState, rejectWithValue }) => {
    const { urlQueryParams } = getState().preferences;
    const hid = getState().preferences?.hid;

    if (urlQueryParams == null || !hid) return rejectWithValue(null);

    const { unsubscribeKeys, jobId } = urlQueryParams;

    if (unsubscribeKeys === 'all') {
      dispatch(unsubscribeAll());
      return [unsubscribeKeys];
    } else if (unsubscribeKeys) {
      await sendUnsubscribeList(hid, unsubscribeKeys, jobId);

      // uncheck all unsubscribed items
      const subscriptionKeys = unsubscribeKeys.split(',');

      const emptyGroupKey = '';
      const keysWithGroup: string[] = [];
      subscriptionKeys.forEach((subscriptionKey) => {
        try {
          const { groupKey, tabKey, itemKey } = findKey(getState().preferences, subscriptionKey);
          keysWithGroup.push(groupKey + ':' + subscriptionKey);
          dispatch(
            setItemState({
              groupKey: groupKey,
              tabKey: tabKey,
              itemKey: itemKey,
              state: TriState.false,
            }),
          );
        } catch {
          // This can occur if the subscription is not listed in the UI
          keysWithGroup.push(emptyGroupKey + ':' + subscriptionKey);
        }
      });

      return keysWithGroup;
    } else {
      return [];
    }
  },
  {
    condition: (_, { getState }): boolean => {
      const { urlQueryParams, status } = getState().preferences;
      return status.status === 'ok' && urlQueryParams?.unsubscribeKeys != null;
    },
  },
);

export const subscribe = createAsyncThunk<string[], void, ThunkApiConfig>(
  PREFERENCES_SUBSCRIBE_PREFIX,
  async (_, { dispatch, getState, rejectWithValue }) => {
    const { urlQueryParams } = getState().preferences;
    const hid = getState().preferences.hid;

    if (urlQueryParams == null || !hid) return rejectWithValue(null);

    const { subscribeKeys } = urlQueryParams;

    if (subscribeKeys == null) return [];

    // check all subscribed items
    const keys = subscribeKeys.split(',');
    const keysWithGroup: string[] = [];
    keys.forEach((key) => {
      const { groupKey, tabKey, itemKey } = findKey(getState().preferences, key);

      keysWithGroup.push(groupKey + ':' + itemKey);

      dispatch(
        setItemState({
          groupKey: groupKey,
          tabKey: tabKey,
          itemKey: itemKey,
          state: TriState.true,
        }),
      );
    });

    const subscriptions = keys.map((key) => ({ key, subscribed: true }));
    await sendPreferencesUpdate(hid, subscriptions);

    return keysWithGroup;
  },
  {
    condition: (_, { getState }): boolean => {
      const { urlQueryParams, status } = getState().preferences;
      return status.status === 'ok' && urlQueryParams?.subscribeKeys != null;
    },
  },
);

export const initUserPreferences = createAsyncThunk<void, { query: string; useSso: boolean }, ThunkApiConfig>(
  PREFERENCES_INITIALIZATION_PREFIX,
  async (args, { dispatch, getState }) => {
    const { urlQueryParams } = getState().preferences;

    if (urlQueryParams == null) return;

    const { hid, source, unsubscribeKeys, subscribeKeys } = urlQueryParams;

    trackLoadPreferencesCenterPage(hid, source, unsubscribeKeys, subscribeKeys);

    // initialize all structures w/info from ConsentHub
    await dispatch(loadUserPreferences(hid));

    batch(() => {
      dispatch(unsubscribe());
      dispatch(subscribe());
    });
  },
);

const requestConsentConfirmation = (update: ListUpdate, preferenceState: PreferencesState): boolean => {
  if (preferenceState.groups['consents'] === undefined) return false;

  const gmoi = preferenceState.groups['consents'].tabs['root'].items['generalMarketingOptIn'];

  if (update.bypassConsentCheck) return false;

  // only check consent if they are consenting or subscribing, not revoking or unsubscribing
  if (update.state !== TriState.true) return false;

  const needsGmoi = gmoi !== undefined && (gmoi == null || gmoi !== ItemStatus.ON);

  for (const item of update.keys) {
    const { groupKey } = findKey(preferenceState, item);

    // Show consent modal if:
    // - Group is 'product_recommendations' OR item is 'generalMarketingOptIn'
    // - AND generalMarketingOptIn currently is null or false.
    // - AND consent modal isn't open
    if (
      (groupKey === 'product_recommendations' || item === 'generalMarketingOptIn') &&
      needsGmoi &&
      !preferenceState.consentDialog.showDialog
    ) {
      return true;
    }
  }
  return false;
};

// Worst name for a function ever
const requestConfirmationForPrUnsub = (update: ListUpdate, preferenceState: PreferencesState): boolean => {
  let anyPrSubbed = false;

  try {
    for (const tabKey of Object.keys(preferenceState.groups['product_recommendations'].tabs)) {
      for (const itemKey of Object.keys(preferenceState.groups['product_recommendations'].tabs[tabKey].items)) {
        if (preferenceState.groups['product_recommendations'].tabs[tabKey].items[itemKey] === ItemStatus.ON) {
          anyPrSubbed = true;
          break;
        }
      }

      if (anyPrSubbed) break;
    }
  } catch {
    // No issue here. Just means PR group is missing.
  }

  if (update.bypassConsentDeclinePrCheck) return false;

  // only request confirmation if they are revoking consent
  if (update.state !== TriState.false) return false;

  for (const item of update.keys) {
    // Show confirmation modal if:
    // - Item is 'generalMarketingOptIn'
    // - AND any product recommendation is subscribed
    // - AND confirmation modal isn't open
    if (item === 'generalMarketingOptIn' && anyPrSubbed && !preferenceState.consentDeclinePrDialog.showDialog)
      return true;
  }

  return false;
};

const requestGlobalUnsubscribe = (preferenceState: PreferencesState): boolean => {
  for (const groupKey of Object.keys(preferenceState.groups)) {
    for (const tabKey of Object.keys(preferenceState.groups[groupKey].tabs)) {
      for (const itemKey of Object.keys(preferenceState.groups[groupKey].tabs[tabKey].items)) {
        const itemStatus = preferenceState.groups[groupKey].tabs[tabKey].items[itemKey];
        if (itemStatus === ItemStatus.ON || itemStatus === ItemStatus.PENDING) {
          return false;
        }
      }
    }
  }

  return true;
};

const showDoubleOptIn = (update: ListUpdate, response: ConsentHubConsentAndSubscriptionResponse): boolean => {
  return !!(
    update.keys.includes('generalMarketingOptIn') &&
    response.groups &&
    response.groups['consents'].tabs['root'].items['generalMarketingOptIn'] === ItemStatus.PENDING
  );
};

const generateConsentsPayload = (prefState: PreferencesState, update: ListUpdate): ConsentHubConsentRequest[] => {
  if (update.state === TriState.busy)
    throw new Error('Attempting to generate ConsentHub consents payload with invalid consent state.');

  const payload: ConsentHubConsentRequest[] = [];

  for (const item of update.keys) {
    const { groupKey, itemKey } = findKey(prefState, item);
    const type = prefState.groups[groupKey].type;

    if (type === GroupType.CONSENTS) {
      let targetState: boolean = false;

      if (update.state === TriState.true) targetState = true;

      payload.push({
        key: itemKey,
        displayedText: 'Changed via Pref. Center UI',
        granted: targetState,
      });
    }
  }

  return payload;
};

const generateSubscriptionsPayload = (
  prefState: PreferencesState,
  update: ListUpdate,
): ConsentHubSubscriptionRequest[] => {
  if (update.state === TriState.busy)
    throw new Error('Attempting to generate ConsentHub subscriptions payload with invalid subscription state.');

  const payload: ConsentHubSubscriptionRequest[] = [];

  for (const item of update.keys) {
    const { groupKey, itemKey } = findKey(prefState, item);
    const type = prefState.groups[groupKey].type;

    if (type === GroupType.SUBSCRIPTIONS) {
      let targetState: boolean = false;

      if (update.state === TriState.true) targetState = true;

      payload.push({
        key: itemKey,
        subscribed: targetState,
      });
    }
  }

  return payload;
};

const setStates = (prefState: PreferencesState, update: ListUpdate, dispatch: AppDispatch, targetState: TriState) => {
  for (const item of Object.values(update.keys)) {
    const { groupKey, tabKey, itemKey } = findKey(prefState, item);

    dispatch(
      setItemState({
        groupKey: groupKey,
        tabKey: tabKey,
        itemKey: itemKey,
        state: targetState,
      }),
    );
  }
};

export const sendItemsFromUI = createAsyncThunk<
  ConsentHubConsentAndSubscriptionResponse | undefined,
  ListUpdate,
  ThunkApiConfig
>(PREFERENCES_SEND_FROM_UI_PREFIX, async (update, { dispatch, getState, rejectWithValue }) => {
  const hid = getState().preferences?.hid;

  if (!hid) return rejectWithValue(null);

  if (requestConsentConfirmation(update, getState().preferences)) {
    dispatch(
      showConsentDialog({
        showDialog: true,
        update: update,
      }),
    );
    return rejectWithValue(null);
  } else if (update.sourceGroup === 'product_recommendations' && update.state === TriState.true) {
    if (update.sourceComponent === 'ItemCard') {
      trackSubscriptionUpdate(true, { source: update.sourceItem });
    } else if (update.sourceComponent === 'GroupContent') {
      trackSubscriptionGroupUpdate(true, { groupName: update.sourceGroup, tabName: update.sourceTab });
    }
  }

  if (requestConfirmationForPrUnsub(update, getState().preferences)) {
    dispatch(
      showConsentDeclinePrDialog({
        showDialog: true,
        update: update,
      }),
    );
    return rejectWithValue(null);
  } else if (update.sourceGroup === 'consents' && update.state === TriState.false) {
    if (update.sourceComponent === 'ItemCard') {
      trackRevokeConsentClick({ source: update.sourceItem });
    } else if (update.sourceComponent === 'GroupContent') {
      trackRevokeAllConsentClick();
    }
  }

  const request: ConsentHubConsentAndSubscriptionRequest = {
    hid: hid,
    formUrl: window.location.href,
    site: 'atlassian',
    consents: generateConsentsPayload(getState().preferences, update),
    subscriptions: generateSubscriptionsPayload(getState().preferences, update),
    source: 'PREFERENCES_CENTER',
    action: 'UPDATE_PREFERENCES',
  };

  try {
    // update checkbox(es) w/spinner for all items in consent/subscription payloads
    setStates(getState().preferences, update, dispatch, TriState.busy);

    const response = await updateConsent(request);

    if (response.downForMaintenanceMessage != null) {
      // don't show the modal error, just refresh for a proper DFM page.
      window.location.reload();
    }

    if (showDoubleOptIn(update, response)) {
      dispatch(
        showDoubleOptInDialog({
          showDialog: true,
        }),
      );
    } else if (!update.skipNotification) {
      dispatch(logSuccessNotification());
    }

    return response;
  } catch {
    // reset checkbox(es)
    let targetState = TriState.false;
    if (update.state === TriState.false) targetState = TriState.true;

    setStates(getState().preferences, update, dispatch, targetState);

    dispatch(logErrorNotification());
  }
});

const findSubscriptionGroup = (keys: string | undefined, state: PreferencesState): VisibleGroup | undefined => {
  if (keys == null || keys === 'all' || keys.includes(',')) return undefined;
  if (state.groups == null) return undefined;

  try {
    const { groupKey, tabKey } = findKey(state, keys);

    return {
      groupKey: groupKey,
      tabKey: tabKey,
    };
  } catch {
    // If we can't find the key, show them all.
    return undefined;
  }
};

const preferencesSlice = createSlice({
  name: PREFERENCES_SLICE_NAME,
  initialState,
  reducers: {
    logSuccessNotification: (state) => {
      const newFlagId = state.notifications.length + 1;
      const newFlagData = getSuccessNotificationData(
        newFlagId,
        'Your preferences were updated. Please be patient, as it can take up to 5 business days for these changes to go into effect.',
      );
      state.notifications.unshift(newFlagData);
    },
    logErrorNotification: (state) => {
      const newFlagId = state.notifications.length + 1;
      const newFlagData = getErrorNotificationData(
        newFlagId,
        'There was an error while updating your preferences. Please try again.',
      );
      state.notifications.unshift(newFlagData);
    },
    removeNotification(state) {
      state.notifications.shift();
    },
    setItemState: (state, action: PayloadAction<ItemUpdateState>) => {
      const { groupKey, tabKey, itemKey, state: targetState } = action.payload;

      switch (targetState) {
        case TriState.true:
          state.groups[groupKey].tabs[tabKey].items[itemKey] = ItemStatus.ON;
          break;

        case TriState.false:
          state.groups[groupKey].tabs[tabKey].items[itemKey] = ItemStatus.OFF;
          break;

        case TriState.busy:
          state.groups[groupKey].tabs[tabKey].items[itemKey] = undefined;
          break;
      }
    },
    closeNotification: (state) => {
      state.actionResult = initialState.actionResult;
    },
    showAllGroups: (state) => {
      state.filter = initialState.filter;
      state.actionResult = initialState.actionResult;
    },
    showConsentDialog: (state, action: PayloadAction<DialogPayload>) => {
      state.consentDialog = action.payload;
    },
    showConsentDeclinePrDialog: (state, action: PayloadAction<DialogPayload>) => {
      state.consentDeclinePrDialog = action.payload;
    },
    showGlobalUnsubscribeDialog: (state, action: PayloadAction<DialogPayload>) => {
      state.globalUnsubscribeDialog = action.payload;
    },
    showDoubleOptInDialog: (state, action: PayloadAction<DialogPayload>) => {
      state.doubleOptInDialog = action.payload;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<PreferencesState>) => {
    builder.addCase(initUserPreferences.pending, (state, { meta }) => {
      const subscriptionReferralUrlParams = parsePreferencesQuery(meta.arg.query);

      if (subscriptionReferralUrlParams.hid || meta.arg.useSso) {
        state.urlQueryParams = subscriptionReferralUrlParams;
      } else {
        state.status = { status: 'not-found' };
      }
    });

    builder.addCase(loadUserPreferences.fulfilled, (state, { payload }) => {
      const { downForMaintenanceMessage, email, groups, hid } = payload;

      if (downForMaintenanceMessage != null) {
        state.status = {
          status: 'down-for-maintenance',
          message: downForMaintenanceMessage,
        };
      } else if (email == null || hid == null) {
        state.status = { status: 'not-found' };
      } else if (groups == null || Object.keys(groups).length === 0) {
        state.email = email;
        state.hid = hid;
        state.status = { status: 'empty' };
      } else {
        state.status = initialState.status;
        state.email = email;
        state.hid = hid;
        state.groups = groups;
        state.filter = findSubscriptionGroup(state.urlQueryParams?.unsubscribeKeys, state) || initialState.filter;

        const initialStates: Record<string, ItemStatus | undefined> = {};
        Object.keys(groups).forEach((groupKey) => {
          Object.keys(groups[groupKey].tabs).forEach((tabKey) => {
            Object.keys(groups[groupKey].tabs[tabKey].items).forEach((itemKey) => {
              initialStates[itemKey] = groups[groupKey].tabs[tabKey].items[itemKey];
            });
          });
        });
        state.initialStates = initialStates;
      }
    });

    builder.addCase(sendItemsFromUI.fulfilled, (state, { payload }) => {
      if (payload === undefined || payload.groups === undefined) return;

      state.groups = payload.groups;

      // Given the received data, determine if GlobalUnsubscribeDialog Modal should be shown.
      if (requestGlobalUnsubscribe(state)) {
        state.globalUnsubscribeDialog = { showDialog: true };
      }
    });

    builder.addCase(loadUserPreferences.rejected, (state, action) => {
      if (action.payload === 401) {
        state.status = { status: 'login-required' };
      } else {
        state.status = { status: 'not-found' };
      }
    });

    builder.addCase(unsubscribeAll.pending, (state) => {
      state.actionResult = initialState.actionResult;
    });

    builder.addCase(unsubscribeAll.fulfilled, (state) => {
      state.actionResult = { key: 'unsubscribeAll', status: 'success' };
      window.scrollTo(0, 0);
    });

    builder.addCase(unsubscribeAll.rejected, (state) => {
      state.actionResult = { key: 'request', status: 'error' };
      window.scrollTo(0, 0);
    });

    builder.addCase(unsubscribe.fulfilled, (state, { payload }) => {
      if (payload.length === 1 && payload[0] === 'all') {
        state.actionResult = { key: 'unsubscribeAll', status: 'success' };
      } else {
        state.actionResult = {
          key: 'unsubscribeKeys',
          status: 'success',
          data: payload,
        };
      }
      window.scrollTo(0, 0);
    });

    builder.addCase(unsubscribe.rejected, (state) => {
      state.actionResult = { key: 'request', status: 'error' };
      window.scrollTo(0, 0);
    });

    builder.addCase(subscribe.fulfilled, (state, { payload }) => {
      state.actionResult = {
        key: 'subscribeKeys',
        status: 'success',
        data: payload,
      };
      window.scrollTo(0, 0);
    });
  },
});

const {
  closeNotification,
  setItemState,
  showAllGroups,
  showConsentDialog,
  showConsentDeclinePrDialog,
  showGlobalUnsubscribeDialog,
  showDoubleOptInDialog,
  logSuccessNotification,
  logErrorNotification,
  removeNotification,
} = preferencesSlice.actions;

export {
  closeNotification,
  setItemState,
  showAllGroups,
  showConsentDialog,
  showConsentDeclinePrDialog,
  showGlobalUnsubscribeDialog,
  showDoubleOptInDialog,
  logSuccessNotification,
  logErrorNotification,
  removeNotification,
};

export default preferencesSlice.reducer;
